import * as ThemeManager from './ThemeManager';
import { Electron } from 'pos/common';
import * as Images from 'pos/Images';

import {
	ActionButtonDefinitionDTO,
	CurrentActionDisplayProfileDTO,
	Res,
	TicketDTO,
	WhenPrint,
	TicketLineDTO,
	TicketLineStatus,
	PreparationOrderDTO,
	SoldProductType,
	SearchMode,
	ActionButtonReferenceDTO
} from 'core/api';

import { SessionProvider } from 'core/infrastructure';
import { ActionButtonDefinition, FnKey } from './ActionButton';

type ActionCommandFunc = () => Promise<void>;

type CustomActionCommands = {
	executeDirectAction: (action: ActionButtonDefinitionDTO & { imageSrc: string }) => Promise<void>;
}

type CommonActionCommands = {
	setProductAvailability: ActionCommandFunc;
	registerInputCashOperation: ActionCommandFunc;
	registerOutputCashOperation: ActionCommandFunc;
	openCashDrawer: ActionCommandFunc;
	printPartialBalance: ActionCommandFunc;
	printTotalBalance: ActionCommandFunc;
	closeDay: ActionCommandFunc;
	findInvoice: ActionCommandFunc;
	configurePriceList: ActionCommandFunc;
	showDeliveryNotes: ActionCommandFunc;
	showSalesOrders: ActionCommandFunc;
	showProductsInPendingSalesOrder: ActionCommandFunc;
	showCashControlStatus: ActionCommandFunc;
	giveChangeFromCashControl: ActionCommandFunc;
	showSystemStatus: ActionCommandFunc;
	configureMenu: ActionCommandFunc;
	showAppointments: ActionCommandFunc;
	printTrainingSession: ActionCommandFunc;
	runAdmin: ActionCommandFunc;
	resetPreparationCounter: ActionCommandFunc;
	selectOpenTicket: ActionCommandFunc;
	minimize: ActionCommandFunc;
	doClocking: ActionCommandFunc;
	showTakeOutOrders: ActionCommandFunc;
	configureSmartMenuOrderComms: ActionCommandFunc;
	showDeliveryPersonStatuses: ActionCommandFunc;
	showCardProcessorAdvancedOptions: ActionCommandFunc;
	hasCashControlNotification: () => boolean;
	hasTakeOutOrdersNotification: () => boolean;

}

type SaleActionCommands = {
	getIntroducedText: () => string;
	clearIntroducedText: () => void;
	getTicket: () => TicketDTO;
	getPreparationOrder: () => Nullable<PreparationOrderDTO>;
	getSelectedLine: () => Nullable<TicketLineDTO>;
	prepareTicket: ActionCommandFunc;
	requestCourses: ActionCommandFunc;
	closeTicket: ActionCommandFunc;
	createRefund: ActionCommandFunc;
	printTicket: ActionCommandFunc;
	viewTicket: ActionCommandFunc;
	moveTicket: ActionCommandFunc;
	changePreparationOrder: ActionCommandFunc;
	prepareCurrentTicketAgain: ActionCommandFunc;
	cancelTicket: ActionCommandFunc;
	setCustomer: ActionCommandFunc;
	showCurrentCourses: ActionCommandFunc;
	setOffer: ActionCommandFunc;
	selectTicketInLocation: ActionCommandFunc;
	parkTicket: ActionCommandFunc;
	splitTicket: ActionCommandFunc;
	setTicketFriendlyName: ActionCommandFunc;
	setTableCompanions: ActionCommandFunc;
	createDeliveryNote: ActionCommandFunc;
	createSalesOrder: ActionCommandFunc;
	setTicketPriceList: ActionCommandFunc;
	setBarcode: ActionCommandFunc;
	viewTicketAuditory: ActionCommandFunc;
	setTicketDiscounts: ActionCommandFunc;
	showReservation: ActionCommandFunc;
	sellByPLU: ActionCommandFunc;
	handleLoyaltyInfo: ActionCommandFunc;
	createLoss: ActionCommandFunc;
	changePrintMode: ActionCommandFunc;
	changeTicketOpener: ActionCommandFunc;
	getProductAvailability: ActionCommandFunc;
	inviteTicketLine: ActionCommandFunc;
	composeUnusualProduct: ActionCommandFunc;
	editTipSettings: ActionCommandFunc;
	setTicketServiceCharge: ActionCommandFunc;
	extractPendingTip: ActionCommandFunc;
	printLastInvoice: ActionCommandFunc;
	searchProductByText: ActionCommandFunc;
	showRecentSalesByCustomer: ActionCommandFunc;
	removeTicketLine: ActionCommandFunc;
	searchTakeOutOrder: ActionCommandFunc;
	completeMenu: ActionCommandFunc;
	startTakeOutOrder: ActionCommandFunc;
	endTakeOutOrder: ActionCommandFunc;
	showTicketPayments: ActionCommandFunc;
	prepareTicketLine: ActionCommandFunc;
	prepareTicketLineAgain: ActionCommandFunc;
	requestOrderedTicketLine: ActionCommandFunc;
	setTicketLinePreparationOrder: ActionCommandFunc;
	setTicketLineAddins: ActionCommandFunc;
	addTicketLineNote: ActionCommandFunc;
	editMenu: ActionCommandFunc;
	selectSaleLocation: ActionCommandFunc;
	closeSession: ActionCommandFunc;
	setTicketLineCreator: ActionCommandFunc;
	setTicketLineDiscount: ActionCommandFunc;
	showProductDetails: ActionCommandFunc;
	adjustIngredients: ActionCommandFunc;
	showScaleTickets: ActionCommandFunc;
	setMinTicketAmount: ActionCommandFunc;
	setNextSeatNumber: ActionCommandFunc;
	editLineSeatNumber: ActionCommandFunc;
	printLastTicketGift: ActionCommandFunc;
	getSearchScreenMode: () => SearchMode;
	toggleSearchScreenMode: () => void;
	getPrintMode: () => WhenPrint;
	createInternalConsumption: ActionCommandFunc;
}

function createSaleButton(btnReference: ActionButtonReferenceDTO, actionCommands: SaleActionCommands): Nullable<ActionButtonDefinition> {

	switch (btnReference.ActionId) {
		case 'ef584278-f107-4548-80c2-c453c3fb9689':
			return { title: Res.Web.PREPARE_TICKET_BTN(), type: btnReference.Category, image: Images.PrepareTicket, action: async () => await actionCommands.prepareTicket() };
		case '729d5bb5-6fe7-4fe2-9785-daaac57b8b28':
			return { title: Res.Web.REQUEST_COURSES_BTN(), type: btnReference.Category, image: Images.RequestCourses, action: async () => await actionCommands.requestCourses() };
		case 'e23c572d-e8cd-40af-8b17-faa492b11c87':
			return { title: Res.Web.CLOSE_TICKET_BTN(), type: btnReference.Category, image: Images.CloseTicket, action: async () => await actionCommands.closeTicket() };
		case '0dcae505-3bd6-4e6b-bb75-ca74d4cbf05f':
			return { title: Res.Web.PRINT_TICKET_BTN(), type: btnReference.Category, image: Images.PrintTicket, action: async () => await actionCommands.printTicket() };
		case 'fc577191-a91e-42ab-b356-6fabfe67936e':
			return { title: Res.Web.VIEW_TICKET_BTN(), type: btnReference.Category, image: Images.ViewTicket, action: async () => await actionCommands.viewTicket() };
		case '9ea742d8-3f03-4fe1-a130-fca1aaa4f026':
			return { title: Res.Web.MOVE_TICKET_BTN(), type: btnReference.Category, image: Images.MoveTicket, action: async () => await actionCommands.moveTicket() };
		case '13950a43-e575-44b8-aca4-b3006a4ed02f':
			return { title: Res.Web.CHANGE_DEFAULT_PREPARATION_ORDER_ACTION_BTN(actionCommands.getPreparationOrder() !== null ? actionCommands.getPreparationOrder()!.Name : Res.Msg.MSG_USE_DEFAULT_PREPARATION_ORDER()), type: btnReference.Category, image: Images.ChangeDefaultPreparationOrder, action: async () => await actionCommands.changePreparationOrder() };
		case 'dec524da-56fe-4b86-815f-2c75b1bb9cbd':
			return { title: Res.Web.PREPARE_TICKET_AGAIN_BTN(), type: btnReference.Category, image: Images.PrepareTicketAgain, action: async () => await actionCommands.prepareCurrentTicketAgain() };
		case 'f074f325-4522-4c93-95ea-316f09724ceb':
			return { title: Res.Web.CANCEL_TICKET_BTN(), type: btnReference.Category, image: Images.CancelTicket, action: async () => await actionCommands.cancelTicket() };
		case '39ae7b3c-83bd-4904-8fa7-8e12a8ff2ddf':
			return { title: Res.Web.CUSTOMER_BTN(), type: btnReference.Category, image: Images.Customer, action: async () => await actionCommands.setCustomer() };
		case 'b76df138-9c30-497e-a24e-a8dd20d82759':
			return { title: Res.Web.GET_COURSES_TO_PREPARE_BTN(), type: btnReference.Category, image: Images.GetCoursesToPrepare, action: async () => await actionCommands.showCurrentCourses() };
		case '5f3e22cb-2b4e-46e6-bf05-168437e35183':
			return { title: Res.Web.OFFER_BTN(), type: btnReference.Category, image: Images.Offer, action: async () => await actionCommands.setOffer() };
		case 'c707ef14-bb93-46dc-9163-701494c156e1':
			return { title: Res.Web.SELECT_TICKET_IN_LOCATION_BTN(), type: btnReference.Category, image: Images.SelectTicketInLocation, action: async () => await actionCommands.selectTicketInLocation() };
		case '763c4f6e-3bc3-4be5-a94f-7b1f355d0260':
			return { title: Res.Web.PARK_TICKET_BTN(), type: btnReference.Category, image: Images.ParkTicket, action: async () => await actionCommands.parkTicket() };
		case '7a96a1ed-2331-4d22-942c-1bd083b6816d':
			return { title: Res.Web.SPLIT_TICKET_BTN(), type: btnReference.Category, image: Images.SplitTicket, action: async () => await actionCommands.splitTicket() };
		case '3208e642-c860-4241-8f84-08cabb99c3e2':
			return { title: Res.Web.TICKET_FRIENDLY_NAME_BTN(), type: btnReference.Category, image: Images.TicketFriendlyName, action: async () => await actionCommands.setTicketFriendlyName() };
		case '3be28070-2151-4be5-802e-be23f849bc3d':
			return { title: Res.Web.TABLE_COMPANIONS_BTN(), type: btnReference.Category, image: Images.TableCompanions, action: async () => await actionCommands.setTableCompanions() };
		case 'ec8b52b8-28b9-4220-ab20-541bda2e3f67':
			return { title: Res.Web.CREATE_DELIVERY_NOTE_BTN(), type: btnReference.Category, image: Images.CreateDeliveryNote, action: async () => await actionCommands.createDeliveryNote() };
		case '109d1a70-2c7a-45c3-8124-cc3de67da464':
			return { title: Res.Web.SET_TICKET_PRICELIST_BTN(), type: btnReference.Category, image: Images.SetTicketPriceList, action: async () => await actionCommands.setTicketPriceList() };
		case 'ccf23fb9-2615-4b49-8ce0-b2ed6c98325c':
			return { title: Res.Web.BARCODE_BTN(), type: btnReference.Category, image: Images.SearchBarcode, action: async () => await actionCommands.setBarcode() };
		case '9dd6685f-846c-442c-a287-e71cdc9a4efd':
			return { title: Res.Web.VIEW_TICKET_AUDITORY_BTN(), type: btnReference.Category, image: Images.ViewTicketAuditory, action: async () => await actionCommands.viewTicketAuditory() };
		case '1b4b4f43-2fb1-4209-84c2-1fb30ba5a3a8':
			return { title: Res.Web.SET_TICKET_DISCOUNTS_BTN(), type: btnReference.Category, image: Images.SetTicketDiscounts, action: async () => await actionCommands.setTicketDiscounts() };
		case 'b2f5fa79-ffb0-4049-8745-38cfb4865fda':
			return { title: Res.Web.ONLINE_RESERVATION_BTN(), type: btnReference.Category, image: Images.OnlineReservation, action: async () => await actionCommands.showReservation() };
		case 'f0cf3005-4f3e-4b4d-9eb7-32b4dce1a0d2':
			return { title: Res.Web.SELL_BY_PLU_BTN(), type: btnReference.Category, image: Images.SellByPLU, action: async () => await actionCommands.sellByPLU() };
		case '170826aa-cc6a-4a8b-b41b-bfbe35b425e5':
			return {
				title: actionCommands.getTicket().HasLoyaltyInfo ? Res.Web.REMOVE_LOYALTY_INFO_ACTION_BTN() : Res.Web.ADD_LOYALTY_INFO_ACTION_BTN(),
				type: btnReference.Category,
				image: actionCommands.getTicket().HasLoyaltyInfo ? Images.RemoveLoyaltyInfo : Images.AddLoyaltyInfo,
				action: actionCommands.handleLoyaltyInfo
			};
		case 'b7f6c3fe-33c4-47a9-ba8b-5b6074131ffc':
			return { title: Res.Web.CREATE_LOSS_BTN(), type: btnReference.Category, image: Images.CreateLoss, action: async () => await actionCommands.createLoss() };
		case '97caa18e-85f9-4297-987a-3a9c2c787f81': {
			switch (actionCommands.getPrintMode()) {
				case WhenPrint.Always:
					return { title: Res.Web.PRINT_ALWAYS_ACTION_BTN(), type: btnReference.Category, image: Images.PrintTicketAlways, action: async () => await actionCommands.changePrintMode() };
				case WhenPrint.AskUser:
					return { title: Res.Web.PRINT_ASK_ACTION_BTN(), type: btnReference.Category, image: Images.PrintTicketAsk, action: async () => await actionCommands.changePrintMode() };
				case WhenPrint.Never:
					return { title: Res.Web.PRINT_NEVER_ACTION_BTN(), type: btnReference.Category, image: Images.PrintTicketNever, action: async () => await actionCommands.changePrintMode() };
				default:
					throw new Error(`No se puede construir un botón para ${SessionProvider.getTicketWhenPrint()}`);
			}
		}
		case 'ce626ec3-8f35-4572-9c29-f0a08b04def4':
			return { title: Res.Web.CHANGE_TICKET_OPENER_USER_ACTION_BTN(), type: btnReference.Category, image: Images.ChangeTicketOpenerUser, action: async () => await actionCommands.changeTicketOpener() };
		case '9b57aa77-7e1d-48af-9208-053e6fd4babd':
			return { title: Res.Web.GET_PRODUCT_AVAILABILITY_BTN(), type: btnReference.Category, image: Images.ProductAvailability, action: async () => await actionCommands.getProductAvailability() };
		case 'ff2c06c1-6219-4455-872a-3d0af324d84e':
			return { title: Res.Web.INVITE_TICKET_LINE_BTN(), type: btnReference.Category, image: Images.InviteTicketLine, action: async () => await actionCommands.inviteTicketLine() };
		case 'd85f0ddc-8192-46bc-a71e-facec6bbd641':
			return { title: Res.Web.COMPOSE_UNUSUAL_PRODUCT_BTN(), type: btnReference.Category, image: Images.ComposeUnusualProduct, action: async () => await actionCommands.composeUnusualProduct() };
		case '00c659b0-5235-47a3-ace8-3f323ea244f2':
			return { title: Res.Web.EDIT_TICKET_TIP(), type: btnReference.Category, image: Images.TipSettings, action: async () => actionCommands.editTipSettings() };
		case 'e920f2eb-848e-4ba1-8c5d-67c1ec239f49':
			return { title: Res.Web.EDIT_TICKET_SERVICE_CHARGE(), type: btnReference.Category, image: Images.ServiceCharge, action: async () => await actionCommands.setTicketServiceCharge() };
		case 'd8cd73b8-b320-44b8-8546-d9e018a2865f':
			return { title: Res.Web.EXTRACT_TIP_TEXT(), type: btnReference.Category, image: Images.ExtractPendingTip, action: async () => actionCommands.extractPendingTip() };
		case '0613e5b0-b628-45d3-8ef4-54d214388cd9':
			return { title: Res.Web.PRINT_LAST_INVOICE_ACTION_BTN(), type: btnReference.Category, image: Images.PrintLastClosedTicket, action: async () => actionCommands.printLastInvoice() };
		case '317ab574-fdd6-4a3c-b05f-2ff524a0dd97':
			return { title: Res.Web.CREATE_REFUND_ACTION_BTN(), type: btnReference.Category, image: Images.RefundTicket, action: async () => actionCommands.createRefund() };
		case '035d009e-9c93-41ec-a3ec-ce72d615a98b':
			return { title: Res.Web.SEARCH_PRODUCT_BY_TEXT_ACTION_BTN(), type: btnReference.Category, image: Images.SearchProductByText, action: async () => actionCommands.searchProductByText() };
		case '5989bd8e-3b73-461b-b56b-442ab021196d':
			return { title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_BTN(), type: btnReference.Category, image: Images.ShowRecentSalesByCustomer, action: async () => actionCommands.showRecentSalesByCustomer() };
		case '4b5fd1d9-b86b-4954-8c80-2fd612e4eef1': {
			const selectedLine = actionCommands.getSelectedLine();
			return {
				title: selectedLine === null || selectedLine.Status === TicketLineStatus.New ? Res.Web.REMOVE_TICKET_LINE_ACTION_BTN() : Res.Web.CANCEL_TICKET_LINE_ACTION_BTN(),
				image: selectedLine === null || selectedLine.Status === TicketLineStatus.New ? Images.DeleteTicketLine : Images.CancelTicketLine,
				type: btnReference.Category,
				action: async () => actionCommands.removeTicketLine()
			};
		}
		case '0661116c-ba13-4063-acf6-73f76f6b31e0':
			return { title: Res.Web.SEARCH_TAKE_OUT_ORDER_ACTION_BTN(), type: btnReference.Category, image: Images.SearchTakeOutOrders, action: async () => actionCommands.searchTakeOutOrder() };
		case '4e6eb96b-fa1e-460f-8d98-4cf6e9f1b73f':
			return { title: Res.Web.CREATE_SALE_ORDER_ACTION_BTN(), type: btnReference.Category, image: Images.CreateSalesOrder, action: async () => actionCommands.createSalesOrder() };
		case 'd007e3c7-3e65-4adf-940c-e0e3c2881841':
			return { title: Res.Web.COMPLETE_MENU_ACTION_BTN(), type: btnReference.Category, image: Images.CompleteMenu, action: async () => actionCommands.completeMenu() };
		case '2ce80fb1-cd8b-431d-8046-84b0d00598d7':
			return {
				title: actionCommands.getTicket().HasPhoneOrder ? Res.Web.END_TAKEOUT_ORDER_BTN() : Res.Web.START_TAKEOUT_ORDER_BTN(),
				type: btnReference.Category,
				image: actionCommands.getTicket().HasPhoneOrder ? Images.EndTakeOutOrder : Images.StartTakeOutOrder,
				action: actionCommands.getTicket().HasPhoneOrder ? async () => actionCommands.endTakeOutOrder() : async () => actionCommands.startTakeOutOrder()
			};
		case '345e638c-0587-40a4-8183-fc396f52ba66':
			return { title: Res.Web.SHOW_TICKET_PAYMENTS_BTN(), type: btnReference.Category, image: Images.ShowPayments, action: async () => actionCommands.showTicketPayments() };
		case 'f3af946d-3941-4e38-8fbe-5be2c2c6fce4': {
			const selectedLine = actionCommands.getSelectedLine();
			const isNewLine = selectedLine === null || selectedLine.Status === TicketLineStatus.New;
			return {
				title: isNewLine ? Res.Web.PREPARE_TICKET_LINE_BTN() : Res.Web.PREPARE_TICKET_LINE_AGAIN_BTN(),
				type: btnReference.Category,
				image: isNewLine ? Images.PrepareTicketLine : Images.PrepareTicketLineAgain,
				action: isNewLine ? async () => actionCommands.prepareTicketLine() : async () => actionCommands.prepareTicketLineAgain()
			};
		}
		case 'e40c290c-685d-4a2f-b616-691f13500409':
			return {
				title: Res.Web.REQUEST_ORDERED_TICKET_LINE_BTN(),
				type: btnReference.Category,
				image: Images.RequestOrderedTicketLine,
				action: async () => actionCommands.requestOrderedTicketLine()
			};
		case 'a8dc8d02-3d4e-4836-81d5-6814ce1b0d9a':
			// set line preparation order
			return {
				title: Res.Web.SET_TICKET_LINE_PREPARATION_ORDER_BTN(),
				type: btnReference.Category,
				image: Images.SetTicketLinePreparationOrder,
				action: async () => actionCommands.setTicketLinePreparationOrder()
			};
		case '70ca4472-2b93-4152-8a51-0e7855b7460b': {
			const selectedLine = actionCommands.getSelectedLine();
			return {
				title: selectedLine?.Type === SoldProductType.MenuHeader ? Res.Web.EDIT_MENU_ACTION_BTN() : Res.Web.ADD_LINE_NOTE_ACTION_BTN(),
				type: btnReference.Category,
				image: selectedLine?.Type === SoldProductType.MenuHeader ? Images.EditMenu : Images.GetNotes,
				action: selectedLine?.Type === SoldProductType.MenuHeader ? async () => actionCommands.editMenu() : async () => actionCommands.addTicketLineNote()
			};
		}

		case '4c9f8a93-76d2-4330-8e4c-7696fb8260c3':
			return { title: Res.Web.SET_TICKET_LINE_ADDINS_ACTION_BTN(), type: btnReference.Category, image: Images.ComposeProduct, action: async () => actionCommands.setTicketLineAddins() };
		case '6bd07a93-79f9-4d78-a82b-1a6009543e79':
			return { title: Res.Web.SELECT_SALE_LOCATION_BTN(), type: btnReference.Category, image: Images.SelectSaleLocation, action: async () => actionCommands.selectSaleLocation() };
		case 'd0a15d1b-76e7-4d0d-a634-f05e9d742900':
			return { title: Res.Web.CLOSE_SESSION_ACTION_BTN(), type: btnReference.Category, image: Images.Exit, action: async () => actionCommands.closeSession() };
		case '8e699a70-0f23-4228-a9c1-4b7c786fb9d4':
			return { title: Res.Web.SET_LINE_CREATOR_BTN(), type: btnReference.Category, image: Images.SetTicketLineCreator, action: async () => actionCommands.setTicketLineCreator() };
		case 'f87600df-2224-448d-9e5f-0630b1ce2b2c':
			return { title: Res.Web.SHOW_PRODUCT_DETAILS_BY_SALEFORMAT_ID_BTN(), type: btnReference.Category, image: Images.ProductDataSheet, action: async () => actionCommands.showProductDetails() };
		case '37760ed5-d867-4b34-ac4b-655c27d9191b':
			return { title: Res.Web.SET_TICKET_LINE_DISCOUNT_BTN(), type: btnReference.Category, image: Images.SetTicketLineDiscount, action: async () => actionCommands.setTicketLineDiscount() };
		case '3c3dad56-0ea8-4d6d-b0ea-abccd1232d51':
			return { title: Res.Web.ADJUST_INGREDIENTS_BTN(), type: btnReference.Category, image: Images.AdjustIngredients, action: async () => actionCommands.adjustIngredients() };
		case '9872314c-e6fd-4f1b-9fe1-96ec5db29d75':
			return { title: Res.Web.SHOW_SCALE_TICKETS_ACTION_BTN(), type: btnReference.Category, image: Images.ScaleTickets, action: async () => actionCommands.showScaleTickets() };
		case '926568e6-ac18-4831-837f-11e983035389':
			return { title: Res.Web.SET_MIN_TICKET_AMOUNT_ACTION_BUTTON(), type: btnReference.Category, image: Images.SetMinTicketAmount, action: async () => actionCommands.setMinTicketAmount() };
		case '5b2f43b0-447e-41eb-b651-937557d7101c':
			return { title: Res.Web.SET_NEXT_SEAT_NUMBER_BTN(), type: btnReference.Category, image: Images.SetNextSeatNumber, action: () => actionCommands.setNextSeatNumber() };
		case 'ad91cf02-260c-4bbf-997b-b41d08214370':
			return { title: Res.Web.EDIT_LINE_SET_NUMBER_BTN(), type: btnReference.Category, image: Images.EditLineSeatNumber, action: () => actionCommands.editLineSeatNumber() };
		case 'cf7edd5a-c072-425a-ba50-da59a8cf0fd5':
			return { title: Res.Web.PRINT_PREVIOUS_GIFT_TICKET_BTN(), type: btnReference.Category, image: Images.TicketGift, action: () => actionCommands.printLastTicketGift() };
		case '9bc84ce2-fcd8-482b-a1c0-9d0772de139b':
			return {
				title: actionCommands.getSearchScreenMode() === SearchMode.Text ? Res.Web.CHANGE_SEARCH_MODE_BY_FAMILY_AND_CATEGORY_ACTION_BTN() : Res.Web.CHANGE_SEARCH_MODE_BY_TEXT_ACTION_BTN(),
				type: btnReference.Category,
				image: actionCommands.getSearchScreenMode() === SearchMode.Text ? Images.SearchByFamiliesAndCategories : Images.SearchByText,
				action: async () => actionCommands.toggleSearchScreenMode()
			};
		case '15847f43-8c7c-49ad-bec0-5517188d7038':
			return { title: Res.Web.INTERNAL_CONSUMPTIONS_BTN(), type: btnReference.Category, image: Images.InternalConsumptions, action: async () => actionCommands.createInternalConsumption() };
		default:
			return null;
	}
}

function createCommonButton(btnReference: ActionButtonReferenceDTO, actionCommands: CommonActionCommands): Nullable<ActionButtonDefinition> {
	switch (btnReference.ActionId) {
		case 'd6b8d2b1-8b67-4540-9bfd-f053380cca0f':
			return { title: Res.Web.CLOSE_DAY_BTN(), type: btnReference.Category, image: Images.CloseDay, action: async () => await actionCommands.closeDay() };
		case 'ac1f78d0-76d5-47af-9192-d4691ebfb944':
			return { title: Res.Web.OPEN_CASH_DRAWER_BTN(), type: btnReference.Category, image: Images.OpenCashDrawer, action: async () => await actionCommands.openCashDrawer() };
		case '2f7ae16d-12d0-4a39-8896-f6ec6f8d2564':
			return { title: Res.Web.FIND_TICKET_BTN(), type: btnReference.Category, image: Images.ShowInvoices, action: async () => await actionCommands.findInvoice() };
		case '13bf4579-2370-4448-a0c6-3f867e0be0a3':
			return { title: Res.Web.SET_PRODUCT_AVAILABILITY_BTN(), type: btnReference.Category, image: Images.ProductAvailability, action: async () => await actionCommands.setProductAvailability() };
		case '1f5528bb-250b-4bdb-93e5-0a9f5f3dd6c9':
			return { title: Res.Web.CONFIGURE_MENU_BTN(), type: btnReference.Category, image: Images.ComposeMenu, action: async () => await actionCommands.configureMenu() };
		case '15da7aa2-0b58-4e71-95d1-3473df7dd7c0':
			return { title: Res.Web.FIND_PENDING_DELIVERY_NOTES_ACTION_BTN(), type: btnReference.Category, image: Images.ShowDeliveryNotes, action: async () => actionCommands.showDeliveryNotes() };
		case '8b31410e-dc74-4253-a362-f90998ead22c':
			return { title: Res.Web.SELECT_OPEN_TICKET_ACTION_BTN(), type: btnReference.Category, image: Images.SelectOpenTicket, action: async () => actionCommands.selectOpenTicket() };
		case '55956d5d-ba8d-4b16-927a-2ee915fff4de':
			return { title: Res.Web.SHOW_APPOINTMENTS_BTN(), type: btnReference.Category, image: Images.ShowAppointments, action: async () => actionCommands.showAppointments() };
		case 'b8dc9bca-4ceb-464f-b88b-7fb877937819':
			return { title: Res.Web.REGISTER_INPUT_CASH_TRANSACTION_BTN(), type: btnReference.Category, image: Images.RegisterInputCashTransaction, action: async () => actionCommands.registerInputCashOperation() };
		case '4d8fa874-4ebb-4fe7-9c5c-54feac2cf114':
			return { title: Res.Web.REGISTER_OUTPUT_CASH_TRANSACTION_BTN(), type: btnReference.Category, image: Images.RegisterOutputCashTransaction, action: async () => actionCommands.registerOutputCashOperation() };
		case '243506db-baea-447d-a8a7-69b59159940c':
			return { title: Res.Web.SHOW_SALES_ORDERS_PREPARATION_BTN(), type: btnReference.Category, image: Images.ShowSalesOrdersPreparation, action: async () => actionCommands.showProductsInPendingSalesOrder() };
		case '6cf7246b-257c-445f-9fb2-17fbe916ddea':
			return { title: Res.Web.CONFIGURE_SMART_MENU_ORDER_COMMS(), type: btnReference.Category, image: Images.ConfigureSmartMenuOrderComms, action: async () => actionCommands.configureSmartMenuOrderComms() };
		case '7ea2e353-67fa-481a-aeb3-1b702b3305a1':
			return { title: Res.Web.SEARCH_SALE_ORDERS_ACTION_BTN(), type: btnReference.Category, image: Images.ShowSalesOrders, action: async () => actionCommands.showSalesOrders() };
		case 'e9787d07-31b9-48b1-986f-181d1912fd5b':
			return { title: Res.Web.SHOW_DELIVERY_PERSON_STATUSES_BTN(), type: btnReference.Category, image: Images.DeliveryPersonBalance, action: async () => actionCommands.showDeliveryPersonStatuses() };
		case 'a7131f37-d9dd-4b29-ba1c-872f13cc6707':
			return { title: Res.Web.MINIMIZE_WINDOW_BTN(), type: btnReference.Category, image: Images.Minimize, action: async () => actionCommands.minimize() };
		case '80ef3cf7-b923-437f-9b3b-85313a02f33a':
			return { title: Res.Web.SHOW_CASH_CONTROL_STATUS_BTN(), type: btnReference.Category, image: actionCommands.hasCashControlNotification() ? Images.CasControlStatusWarning : Images.CashControlStatus, action: async () => actionCommands.showCashControlStatus() };
		case 'bf6adbea-fa4e-426e-9e78-39186202e3a0':
			return { title: Res.Web.GIVE_CHANGE_CASH_CONTROL_ACTION_BTN(), type: btnReference.Category, image: Images.GiveChangeFromCashControl, action: async () => actionCommands.giveChangeFromCashControl() };
		case '5232c142-714f-41d6-ba24-038bdc0a79a0':
			return { title: Res.Web.RESET_PREPARATION_COUNTER_ACTION_BTN(), type: btnReference.Category, image: Images.PreparationCounter, action: async () => actionCommands.resetPreparationCounter() };
		case '312577b3-7e04-4291-a17a-39c17901f810':
			return { title: Res.Web.PRINT_TRAINING_SESSION_ACTION_BTN(), type: btnReference.Category, image: Images.TrainingSession, action: async () => actionCommands.printTrainingSession() };
		case '63f310ff-7719-4fc1-93d3-db06449c88b6':
			return { title: Res.Web.SHOW_SYSTEM_STATE_ACTION_BTN(), type: btnReference.Category, image: Images.ShowSystemStatus, action: async () => actionCommands.showSystemStatus() };
		case '04f99d74-8ce1-4825-9730-8ac526c43045':
			return { title: Res.Web.SELECT_USER_ATTENDANCE_BTN(), type: btnReference.Category, image: Images.Clocking, action: async () => actionCommands.doClocking() };
		case '46afb238-6736-42ed-bf62-c48755aa364f':
			return { title: Res.Web.CONFIGURE_PRICE_LIST_ACTION_BTN(), type: btnReference.Category, image: Images.ConfigurePriceList, action: async () => actionCommands.configurePriceList() };
		case '65bbcf8f-e7a9-4c11-ae6d-518d5f0d83c2':
			return { title: Res.Web.RUN_ADMIN_BTN(), type: btnReference.Category, image: Images.RUN_ADMIN, action: async () => actionCommands.runAdmin() };
		case '1ed093e4-4eb7-4f28-8470-a071eb69f37c':
			return { title: Res.Web.PRINT_PARTIAL_BALANCE_BTN(), type: btnReference.Category, image: Images.PrintPartialBalance, action: async () => actionCommands.printPartialBalance() };
		case '86db8aa7-451e-4df8-90e1-bbda92f0324e':
			return { title: Res.Web.PRINT_TOTAL_BALANCE_BTN(), type: btnReference.Category, image: Images.PrintTotalBalance, action: async () => actionCommands.printTotalBalance() };
		case '7e185bfe-68a0-446a-b625-550a24ddb2d2':
			return { title: Res.Web.SHOW_CARDPROCESSOR_ADVANCED_OPTIONS_BTN(), type: btnReference.Category, image: Images.ShowCardProcessorAdvancedOptions, action: async () => actionCommands.showCardProcessorAdvancedOptions() };
		case '40b42eb9-fda8-4a01-bba0-87cd8649d877':
			return {
				title: Res.Web.SHOW_TAKE_OUT_ORDERS_ACTION_BTN(),
				type: btnReference.Category,
				image: actionCommands.hasTakeOutOrdersNotification() ? Images.ShowTakeOutOrders_New : Images.ShowTakeOutOrders,
				action: async () => actionCommands.showTakeOutOrders()
			};
		default:
			return null;
	}
}


function createDirectActionButton(btnReference: ActionButtonReferenceDTO, definitions: ActionButtonDefinitionDTO[], commands: CustomActionCommands): Nullable<ActionButtonDefinition> {
	const definition = definitions.find(x => x.ActionId === btnReference.ActionId);

	if (!definition)
		return null;

	const imageAsBase64 = ThemeManager.getCurrentTheme().value === 'dark' ? definition.DarkImageAsBase64 : definition.LightImageAsBase64;
	const imageSrc = imageAsBase64 ? `data:image/*;base64,${imageAsBase64}` : '';

	return {
		title: definition.Text,
		image: imageSrc,
		type: definition.Category,
		action: async () => await commands.executeDirectAction({ ...definition, imageSrc })
	};
}

export type SaleScreenActionCommands = CustomActionCommands & CommonActionCommands & SaleActionCommands
export type SaleLocationActionCommands = CustomActionCommands & CommonActionCommands;


function createButton(btnReference: ActionButtonReferenceDTO, customActionButtonDefs: ActionButtonDefinitionDTO[], commands: SaleLocationActionCommands | SaleScreenActionCommands): Nullable<ActionButtonDefinition> {

	function isCommonActionCommand(commands: SaleActionCommands | CommonActionCommands | CustomActionCommands): commands is CommonActionCommands {
		return (commands as any).getTicket === undefined;
	}

	// Null Button
	if (btnReference.ActionId === 'aca17932-e0ef-4313-a193-a186b8381bb3')
		return null;

	// Si es botón personalizado, lo creo
	const directAction = createDirectActionButton(btnReference, customActionButtonDefs, commands);
	if (directAction)
		return directAction;

	if (isCommonActionCommand(commands))
		return createCommonButton(btnReference, commands);

	// Si los commands son de venta, el botón puede ser de venta o común
	return createSaleButton(btnReference, commands) || createCommonButton(btnReference, commands);
}

type SaleScreenButtons = {
	quickButtons: Nullable<ActionButtonDefinition>[],
	otherButtons: Nullable<ActionButtonDefinition>[]
};

export function createForSelectSaleLocationScreen(directActionButtons: ActionButtonDefinitionDTO[], profile: CurrentActionDisplayProfileDTO, commands: SaleLocationActionCommands): Nullable<ActionButtonDefinition>[] {
	return profile.SelectSaleLocationActions.map(btnReference => createButton(btnReference, directActionButtons, commands));
}

export function createForSaleScreen(directActionButtons: ActionButtonDefinitionDTO[], profile: CurrentActionDisplayProfileDTO, commands: SaleScreenActionCommands): SaleScreenButtons {
	const quickButtons = profile.QuickActions.map(btnReference => createButton(btnReference, directActionButtons, commands));

	// Los quickaction buttons les colocamos los fnKeys
	if (Electron.isEnabled())
		for (let index = 0; index < 12 && index < quickButtons.length; index++)
			if (quickButtons[index] !== null)
				quickButtons[index]!.fnKey = `F${index + 1}` as FnKey;

	const otherButtons = profile.OtherActions.map(guid => createButton(guid, directActionButtons, commands));
	return { quickButtons, otherButtons };
}
